import { useState, useEffect } from "react";
import { format } from "date-fns";
import { Lock, RotateCcw, Minus, Plus, Cloud } from "lucide-react";
import { PHASES } from "@/lib/lunelle-data";
import { useCycle, avgCycleLength, pregnancyInfo, learnedPeriodLength } from "@/lib/cycle";
import { Reveal, Eyebrow } from "./Reveal";
import { MonthGrid } from "./MonthGrid";
import { DayPanel } from "./DayPanel";
import { TodayCard } from "./TodayCard";
import { SymptomInsights } from "./SymptomInsights";
import { useAccount, useProfile } from "@/lib/account";
import { useFaith } from "@/lib/useFaith";
import { FaithDayNote } from "./FaithDayNote";

const SaveStatus = () => {
  const { user, openAuth } = useAccount();
  if (user) return <span className="flex items-center gap-1.5" data-testid="cycle-save-status"><Cloud className="h-3.5 w-3.5" /> Saved to your account</span>;
  return (
    <span className="flex flex-wrap items-center gap-1.5" data-testid="cycle-save-status"><Lock className="h-3.5 w-3.5" /> Saved only in this browser ·
      <button type="button" onClick={() => openAuth("signin")} data-testid="cycle-sign-in-link" className="font-semibold text-[#9E4429] underline-offset-2 hover:underline">Sign in to sync</button>
    </span>
  );
};

const StepBtn = ({ testId, label, onClick, disabled, children }) => (
  <button type="button" onClick={onClick} disabled={disabled} aria-label={label} data-testid={testId}
    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#C86D51]/25 bg-[#FDFBF7] text-[#9E4429] transition-colors hover:bg-[#F3E6DA] disabled:opacity-40">{children}</button>
);

const Stepper = ({ id, label, value, min, max, onChange }) => {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => { setDraft(String(value)); }, [value]);
  const clamp = (n) => Math.min(max, Math.max(min, n));
  const commit = () => {
    const n = parseInt(draft, 10);
    if (Number.isNaN(n)) { setDraft(String(value)); return; }
    const v = clamp(n);
    setDraft(String(v));
    if (v !== value) onChange(v);
  };
  const step = (d) => onChange(clamp((Number(value) || min) + d));
  return (
    <div className="flex items-center justify-between gap-3 text-sm text-[#5C4A42]">
      <label htmlFor={`cycle-input-${id}`}>{label} <span className="block text-xs text-[#6B5A52]">{min}–{max} days</span></label>
      <div className="flex items-center gap-2">
        <StepBtn testId={`cycle-dec-${id}`} label={`Decrease ${label}`} onClick={() => step(-1)} disabled={value <= min}><Minus className="h-4 w-4" /></StepBtn>
        <input id={`cycle-input-${id}`} type="text" inputMode="numeric" pattern="[0-9]*" value={draft} data-testid={`cycle-input-${id}`}
          onChange={(e) => setDraft(e.target.value.replace(/\D/g, "").slice(0, 2))}
          onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
          className="h-10 w-16 rounded-xl border border-[#C86D51]/20 bg-[#FDFBF7] px-2 text-center font-serif text-xl text-[#2C221E] outline-none focus:border-[#9E4429]" />
        <StepBtn testId={`cycle-inc-${id}`} label={`Increase ${label}`} onClick={() => step(1)} disabled={value >= max}><Plus className="h-4 w-4" /></StepBtn>
      </div>
    </div>
  );
};

const ModeSwitch = ({ pregnant, onChange }) => (
  <div className="mt-6 grid grid-cols-2 gap-1 rounded-full border border-[#C86D51]/20 bg-[#FDFBF7] p-1" role="tablist">
    {[[false, "Cycle"], [true, "Pregnancy"]].map(([val, label]) => (
      <button key={label} onClick={() => onChange(val)} data-testid={`cycle-mode-${label.toLowerCase()}`}
        className={`rounded-full py-2 text-sm font-medium transition-colors ${pregnant === val ? "bg-[#2C221E] text-[#FBF5EE]" : "text-[#5C4A42]"}`}>{label}</button>
    ))}
  </div>
);

const Legend = ({ pregnant, faith }) => (
  <div className="mt-5 flex flex-wrap gap-x-4 gap-y-2 text-xs text-[#5C4A42]">
    {pregnant ? <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-[#9E4429]" />Estimated due date</span>
      : PHASES.map((p) => <span key={p.id} className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: p.color }} />{p.name}</span>)}
    {!pregnant && <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-[#3F5E57]" />Fertile window</span>}
    <span className="flex items-center gap-1.5"><span className="h-1 w-1 rounded-full bg-[#2C221E]" />Logged day</span>
    {faith && <span className="flex items-center gap-1.5" data-testid="legend-faith-fast"><span className="h-1.5 w-1.5 rotate-45 bg-[#D4A23C]" />Fasting day</span>}
    {faith && <span className="flex items-center gap-1.5" data-testid="legend-faith-holy"><span className="h-1.5 w-1.5 rotate-45 bg-[#6B4C8A]" />Holy day</span>}
  </div>
);

export const CycleCalendar = () => {
  const { cycle, toggleStart, set, setLog, reset } = useCycle();
  const [selected, setSelected] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const preg = pregnancyInfo(cycle);
  const learned = learnedPeriodLength(cycle);
  const { member } = useProfile();
  const [myFaith] = useFaith();
  const faith = member ? myFaith : null;
  return (
    <section id="calendar" className="relative border-t border-[#C86D51]/15 py-20 sm:py-24" data-testid="cycle-calendar-section">
      <div className="mx-auto grid max-w-7xl gap-10 px-5 sm:px-8 lg:grid-cols-[1fr_1.1fr] lg:gap-16 lg:px-12">
        <Reveal className="min-w-0">
          <Eyebrow>Your cycle calendar</Eyebrow>
          <h2 className="mt-5 font-serif text-4xl font-light leading-tight text-[#2C221E] sm:text-5xl">Keep Lunelle in sync <em className="italic text-[#9E4429]">with your month.</em></h2>
          <p className="mt-4 max-w-md text-[#5C4A42]">Tap any day to log your period, flow, mood and symptoms. Lunelle works out your phase so your food, style, budget and demo match where you really are.</p>
          <ModeSwitch pregnant={cycle.pregnant} onChange={(v) => set("pregnant", v)} />
          <TodayCard cycle={cycle} />
          <div className="mt-6 space-y-3">
            {cycle.pregnant ? (
              <label className="flex items-center justify-between gap-3 text-sm text-[#5C4A42]">
                First day of last period
                <input type="date" value={cycle.lmp || preg?.lmp || ""} max={format(new Date(), "yyyy-MM-dd")} onChange={(e) => set("lmp", e.target.value || null)}
                  data-testid="pregnancy-lmp-input" className="h-10 rounded-xl border border-[#C86D51]/20 bg-[#FDFBF7] px-3 text-sm text-[#2C221E] outline-none focus:border-[#9E4429]" />
              </label>
            ) : (
              <>
                <Stepper id="cycle-length" label={cycle.starts.length > 1 ? `Cycle length (your average: ${avgCycleLength(cycle)})` : "Cycle length (days)"} value={Number(cycle.cycleLength) || 28} min={15} max={60} onChange={(v) => set("cycleLength", v)} />
                <Stepper id="period-length" label={learned ? `Period length (your average: ${learned.avg})` : "Period length (days)"} value={Number(cycle.periodLength) || 5} min={1} max={15} onChange={(v) => set("periodLength", v)} />
                <p className="text-xs text-[#6B5A52]" data-testid="period-length-learning-hint">
                  {learned ? `Learned from ${learned.count} logged period${learned.count > 1 ? "s" : ""}. Lunelle uses this for your predictions.` : "Log your flow on each day of your period and Lunelle will learn your usual length."}
                </p>
              </>
            )}
          </div>
          <div className="mt-5 flex items-center justify-between text-xs text-[#6B5A52]">
            <SaveStatus />
            <button onClick={reset} data-testid="cycle-reset-button" className="flex items-center gap-1.5 font-semibold text-[#9E4429]"><RotateCcw className="h-3.5 w-3.5" /> Clear</button>
          </div>
          {!cycle.pregnant && <SymptomInsights cycle={cycle} />}
        </Reveal>
        <Reveal delay={0.1} className="min-w-0">
          <div className="rounded-[2rem] border border-[#C86D51]/15 bg-white/60 p-5 sm:p-7">
            <MonthGrid cycle={cycle} selected={selected} onSelect={setSelected} dueIso={cycle.pregnant ? preg?.dueIso : null} faith={faith} />
            <Legend pregnant={cycle.pregnant} faith={faith} />
            <FaithDayNote faith={faith} iso={selected} cycle={cycle} />
            <DayPanel iso={selected} cycle={cycle} toggleStart={toggleStart} setLog={setLog} />
            <p className="mt-5 text-xs leading-relaxed text-[#6B5A52]">Future days, fertile windows and due dates are estimates. Not for contraception or medical decisions; talk to your doctor or midwife.</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
